import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ToastContainer } from '../components/ui/Toast';
import type { ToastItem, ToastVariant } from '../components/ui/Toast';

interface ToastContextType {
  toast: {
    success: (message: string, duration?: number) => void;
    error: (message: string, duration?: number) => void;
    warning: (message: string, duration?: number) => void;
    info: (message: string, duration?: number) => void;
  };
  showToast: (options: { title?: string; message: string; type?: 'success' | 'error' | 'warning' | 'info'; duration?: number }) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Appends a new toast notification, avoiding rapid identical duplicates
  const addToast = useCallback((message: string, variant: ToastVariant, duration?: number) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
    setToasts((prev) => {
      // Discard duplicate identical notifications while one is already active
      if (prev.some((t) => t.message === message && t.variant === variant)) {
        return prev;
      }
      // Keep a max of 4 toasts at once to prevent viewport overflow
      const trimmed = prev.length >= 4 ? prev.slice(prev.length - 3) : prev;
      return [...trimmed, { id, message, variant, duration }];
    });
  }, []);

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Memoized toast methods ensuring stable object reference across renders
  const toast = useMemo(
    () => ({
      success: (message: string, duration?: number) => addToast(message, 'success', duration),
      error:   (message: string, duration?: number) => addToast(message, 'error',   duration),
      warning: (message: string, duration?: number) => addToast(message, 'warning', duration),
      info:    (message: string, duration?: number) => addToast(message, 'info',    duration),
    }),
    [addToast]
  );

  const showToast = useCallback(
    (options: { title?: string; message: string; type?: 'success' | 'error' | 'warning' | 'info'; duration?: number }) => {
      const variant = (options.type || 'info') as ToastVariant;
      const msg = options.title ? `${options.title}: ${options.message}` : options.message;
      addToast(msg, variant, options.duration);
    },
    [addToast]
  );

  const contextValue = useMemo(() => ({ toast, showToast }), [toast, showToast]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
